import { Injectable } from '@angular/core';

export interface PerformanceMetrics {
  conversionRate: number;
  totalReservations: number;
  totalCustomers: number;
  totalTerritories: number;
  totalContracts?: number;
  contractValue?: number;
}

export interface PerformanceBreakdown {
  totalScore: number;
  breakdown: {
    conversion: PerformanceComponent;
    activity: PerformanceComponent;
    customer: PerformanceComponent;
    territory: PerformanceComponent;
  };
}

export interface PerformanceComponent {
  raw: number;
  normalized: number;
  weighted: number;
  weight: number;
  label: string;
  description: string;
}

@Injectable({
  providedIn: 'root'
})
export class PerformanceScoreService {
  
  // Centralized performance calculation weights
  private readonly WEIGHTS = {
    CONVERSION: 0.4,   // 40% - Most important metric
    ACTIVITY: 0.3,     // 30% - Activity level
    CUSTOMER: 0.2,     // 20% - Customer base
    TERRITORY: 0.1     // 10% - Territory coverage
  };

  // Normalization thresholds (what counts as 100% for each metric)
  private readonly THRESHOLDS = {
    ACTIVITY: 20,      // 20+ reservations = 100%
    CUSTOMER: 50,      // 50+ customers = 100%
    TERRITORY: 5       // 5+ territories = 100%
  };

  /**
   * Calculate performance score with detailed breakdown
   */
  calculatePerformanceScore(metrics: PerformanceMetrics): PerformanceBreakdown {
    // Normalize raw values to 0-100 scale
    const normalizedConversion = Math.min(metrics.conversionRate, 100);
    const normalizedActivity = Math.min((metrics.totalReservations / this.THRESHOLDS.ACTIVITY) * 100, 100);
    const normalizedCustomer = Math.min((metrics.totalCustomers / this.THRESHOLDS.CUSTOMER) * 100, 100);
    const normalizedTerritory = Math.min((metrics.totalTerritories / this.THRESHOLDS.TERRITORY) * 100, 100);

    // Calculate weighted scores
    const weightedConversion = normalizedConversion * this.WEIGHTS.CONVERSION;
    const weightedActivity = normalizedActivity * this.WEIGHTS.ACTIVITY;
    const weightedCustomer = normalizedCustomer * this.WEIGHTS.CUSTOMER;
    const weightedTerritory = normalizedTerritory * this.WEIGHTS.TERRITORY;

    // Calculate total score
    const totalScore = Math.round(weightedConversion + weightedActivity + weightedCustomer + weightedTerritory);

    return {
      totalScore,
      breakdown: {
        conversion: {
          raw: Math.round(metrics.conversionRate),
          normalized: Math.round(normalizedConversion),
          weighted: Math.round(weightedConversion),
          weight: this.WEIGHTS.CONVERSION * 100,
          label: 'Conversion Rate',
          description: 'Reservations converted to contracts'
        },
        activity: {
          raw: metrics.totalReservations,
          normalized: Math.round(normalizedActivity),
          weighted: Math.round(weightedActivity),
          weight: this.WEIGHTS.ACTIVITY * 100,
          label: 'Activity Level',
          description: 'Total reservations created'
        },
        customer: {
          raw: metrics.totalCustomers,
          normalized: Math.round(normalizedCustomer),
          weighted: Math.round(weightedCustomer),
          weight: this.WEIGHTS.CUSTOMER * 100,
          label: 'Customer Base',
          description: 'Unique customers served'
        },
        territory: {
          raw: metrics.totalTerritories,
          normalized: Math.round(normalizedTerritory),
          weighted: Math.round(weightedTerritory),
          weight: this.WEIGHTS.TERRITORY * 100,
          label: 'Territory Coverage',
          description: 'Assigned territories'
        }
      }
    };
  }

  /**
   * Get performance score color based on score value
   */
  getPerformanceScoreColor(score: number): string {
    if (score >= 90) return '#10B981'; // green
    if (score >= 70) return '#F59E0B'; // yellow
    if (score >= 50) return '#3B82F6'; // blue
    return '#EF4444'; // red
  }

  /**
   * Get performance score CSS class for text color
   */
  getPerformanceScoreClass(score: number): string {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    if (score >= 50) return 'text-blue-600';
    return 'text-red-600';
  }

  /**
   * Get performance level description
   */
  getPerformanceLevel(score: number): string {
    if (score >= 90) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Average';
    return 'Needs Improvement';
  }

  /**
   * Calculate simple performance score (for backward compatibility)
   */
  calculateSimpleScore(metrics: PerformanceMetrics): number {
    return this.calculatePerformanceScore(metrics).totalScore;
  }

  /**
   * Get weights configuration for display
   */
  getWeights() {
    return {
      conversion: this.WEIGHTS.CONVERSION * 100,
      activity: this.WEIGHTS.ACTIVITY * 100,
      customer: this.WEIGHTS.CUSTOMER * 100,
      territory: this.WEIGHTS.TERRITORY * 100
    };
  }

  /**
   * Get thresholds configuration for display
   */
  getThresholds() {
    return this.THRESHOLDS;
  }
}

